/**
 * Web Push subscription lifecycle (VAPID + backend storage).
 * Background notifications are delivered via the service worker `push` event from the server.
 */

import http from '../api/http';
import { getApiBaseUrl } from '../config/api.config';
import { getNotificationPermission, isPushSupported } from '../utils/notifications';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Wait for the PWA service worker; scope should be site root `/`. */
export async function waitForServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }
  const registration = await navigator.serviceWorker.ready;
  const scopePath = new URL(registration.scope).pathname;
  if (scopePath !== '/') {
    console.warn('[WebPush] Service worker scope is not root "/":', registration.scope);
  }
  return registration;
}

export async function fetchVapidPublicKey(): Promise<string | null> {
  const res = await fetch(`${getApiBaseUrl()}/v1/push/vapid-public`);
  if (!res.ok) return null;
  const data = (await res.json()) as { vapid_public_key?: string | null };
  return data.vapid_public_key ?? null;
}

export async function savePushSubscriptionToBackend(sub: PushSubscriptionJSON): Promise<boolean> {
  if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys?.auth) return false;

  await http.post('/v1/settings/push-subscription', {
    endpoint: sub.endpoint,
    keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
    user_agent: navigator.userAgent,
  });
  return true;
}

/**
 * Subscribe with pushManager + VAPID and persist on the server.
 * Call when the user is authenticated and notification permission is granted.
 */
export async function registerWebPushSubscription(): Promise<boolean> {
  if (!isPushSupported() || getNotificationPermission() !== 'granted') return false;
  if (!localStorage.getItem('token')) return false;

  try {
    const registration = await waitForServiceWorkerRegistration();
    const vapidPublicKey = await fetchVapidPublicKey();
    if (!vapidPublicKey) return false;

    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey) as BufferSource;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    return await savePushSubscriptionToBackend(subscription.toJSON());
  } catch (err) {
    console.warn('[WebPush] Subscription failed:', err);
    return false;
  }
}

export async function hasActivePushSubscription(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

export async function unsubscribeWebPush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch {
    // ignore
  }
}
